import { useEffect, useState, type ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { DEMO_ACCESS_MESSAGES } from '@/lib/demoAccess'
import { DemoCodeScreen } from './DemoCodeScreen'
import { checkDemoToken, forgetStoredToken, readStoredToken, storeToken } from './demoAccessApi'

type GateState =
    | { status: 'checking' }
    | { status: 'locked'; error?: string }
    | { status: 'open' }

// Keeps the demo behind the access code. In doubt (no token, a refused token,
// the function unreachable) the gate stays closed.
export const DemoGate = ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState<GateState>(() =>
        readStoredToken() ? { status: 'checking' } : { status: 'locked' },
    )

    useEffect(() => {
        if (state.status !== 'checking') return
        const token = readStoredToken()
        if (!token) {
            setState({ status: 'locked' })
            return
        }
        let cancelled = false
        checkDemoToken(token).then((result) => {
            if (cancelled) return
            if (result.ok) {
                setState({ status: 'open' })
                return
            }
            if (result.failure === 'invalid') {
                forgetStoredToken()
                setState({ status: 'locked' })
                return
            }
            setState({ status: 'locked', error: DEMO_ACCESS_MESSAGES.unavailable })
        })
        return () => { cancelled = true }
    }, [state.status])

    if (state.status === 'checking') {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        )
    }

    if (state.status === 'locked') {
        return (
            <DemoCodeScreen
                initialError={state.error}
                onUnlock={(token) => {
                    storeToken(token)
                    setState({ status: 'open' })
                }}
            />
        )
    }

    return <>{children}</>
}
